/**
 * Avialite LED Service
 * Handles communication with Kapitani Middleware for LED device management
 * 
 * API Documentation: Based on IE-Middleware-Avialite
 */

import { IOT_VENDORS, KAPITANI_BASE_URL } from '../config/iotVendors';

// Get Avialite config from centralized IoT vendor configuration
const AVIALITE_VENDOR = IOT_VENDORS.AVIALITE;

const AVIALITE_CONFIG = {
  // Kapitani API Base URL
  baseUrl: KAPITANI_BASE_URL,
  
  // Client ID for Smart Farm Platform (from IoT Vendor config)
  clientId: AVIALITE_VENDOR.clientId,
  
  // Vendor ID for Avialite-SmartFarm (from IoT Vendor config)
  vendorId: AVIALITE_VENDOR.vendorId,
  
  // Debug mode
  debug: process.env.REACT_APP_DEBUG_LED === 'true'
};

// The middleware can hang without ever answering, so the list request is time-boxed.
const LIST_TIMEOUT_MS = 15000;

// A control command makes the middleware wait about 120 s for an Avialite device
// to confirm before it answers, so the browser waits a little longer than that
// for the middleware's own decision but never hangs.
const CONTROL_TIMEOUT_MS = 130000;

class AvialiteService {
  constructor() {
    this.baseUrl = AVIALITE_CONFIG.baseUrl;
    this.clientId = AVIALITE_CONFIG.clientId;
    this.vendorId = AVIALITE_CONFIG.vendorId;
    this.debug = AVIALITE_CONFIG.debug;
  }

  /**
   * API 1: Register a new LED device
   * Endpoint: POST /client/{clientId}/register
   * 
   * @param {Object} ledData - LED registration data
   * @param {string} ledData.ledId - LED identifier (e.g., "LED001")
   * @param {string} ledData.stationId - Station identifier (e.g., "STA001")
   * @param {string} ledData.gatewayId - Gateway device ID (e.g., "GW001")
   * @param {string} ledData.note - Description/notes (optional)
   * @param {string} ledData.farmerId - Farmer ID
   * @param {string} ledData.farmId - Farm ID
   * @returns {Promise<{status: string, id: string, timeStamp: string}>}
   */
  async registerLED(ledData) {
    try {
      if (this.debug) {
        console.log('🚀 [LED Service] Registering LED device:', ledData.ledId);
        console.log('🔗 [LED Service] Endpoint:', `${this.baseUrl}/client/${this.clientId}/register`);
      }
      
      const payload = {
        ledId: ledData.ledId,
        stationId: ledData.stationId,
        gatewayId: ledData.gatewayId,
        note: ledData.note || '',
        farmerId: ledData.farmerId, // Fixed: camelCase farmerId
        farmId: ledData.farmId,
        vendorId: this.vendorId
      };
      
      if (this.debug) {
        console.log('📤 [LED Service] Payload:', payload);
      }
      
      const url = `${this.baseUrl}/client/${this.clientId}/register`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (this.debug) {
        console.log('📥 [LED Service] Response status:', response.status);
      }
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      if (this.debug) {
        console.log('✅ [LED Service] LED registered successfully:', result);
      }
      
      return {
        success: true,
        message: result.message || 'LED registered successfully',
        data: result
      };
      
    } catch (error) {
      console.error('❌ [LED Service] Error registering LED:', error);
      return {
        success: false,
        message: error.message || 'Failed to register LED',
        error: error
      };
    }
  }
  
  /**
   * API 2: List all LEDs for a farm
   * Endpoint: GET /client/{clientId}/device-list
   * 
   * @param {string} farmId - Farm ID
   * @param {string} farmerId - Farmer ID
   * @returns {Promise<Array<{ledId: string, stationId: string, note: string, id: string}>>}
   */
  async listLEDs(farmId, farmerId) {
    // Abort the request when the middleware does not answer in time
    // (same approach as havvaService.request()).
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, LIST_TIMEOUT_MS);

    try {
      if (this.debug) {
        console.log('📋 [LED Service] Fetching LED list for farm:', farmId);
      }
      
      // Use query parameters for GET request
      const params = new URLSearchParams({
        vendorId: this.vendorId,
        farmerId: farmerId || ''
      });
      // Only add farmId if it's a specific farm (not 'ALL' or 'all')
      if (farmId && farmId !== 'ALL' && farmId !== 'all') {
        params.set('farmId', farmId);
      }
      
      const url = `${this.baseUrl}/client/${this.clientId}/device-list?${params}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (this.debug) {
        console.log(`✅ [LED Service] Retrieved ${result?.length || 0} LEDs`);
      }
      
      return {
        success: true,
        data: result
      };
      
    } catch (error) {
      console.error('❌ [LED Service] Error listing LEDs:', error);
      return {
        success: false,
        message: timedOut
          ? `The LED service did not respond within ${Math.round(LIST_TIMEOUT_MS / 1000)} seconds.`
          : (error.message || 'Failed to list LEDs'),
        error: error
      };
    } finally {
      clearTimeout(timer);
    }
  }
  
  /**
   * API 3: Edit LED details
   * Endpoint: PATCH /client/{clientId}/edit
   * 
   * @param {Object} ledData - LED data to update
   * @param {string} ledData.id - Device ID (REQUIRED)
   * @returns {Promise<{status: string}>}
   */
  async editLED(ledData) {
    try {
      if (this.debug) {
        console.log('✏️ [LED Service] Editing LED device:', ledData.ledId);
      }
      
      const payload = {
        id: ledData.id, // REQUIRED field
        ledId: ledData.ledId,
        stationId: ledData.stationId,
        gatewayId: ledData.gatewayId,
        note: ledData.note,
        farmerId: ledData.farmerId, // Fixed: camelCase farmerId
        farmId: ledData.farmId,
        vendorId: this.vendorId
      };
      
      const url = `${this.baseUrl}/client/${this.clientId}/edit`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (this.debug) {
        console.log('✅ [LED Service] LED edited successfully');
      }
      
      return {
        success: true,
        message: result.message || 'LED updated successfully',
        data: result
      };
      
    } catch (error) {
      console.error('❌ [LED Service] Error editing LED:', error);
      return {
        success: false,
        message: error.message || 'Failed to edit LED',
        error: error
      };
    }
  }
  
  /**
   * API 4: Delete LED device
   * Endpoint: DELETE /client/{clientId}/delete
   * 
   * @param {string} id - Device ID to delete
   * @returns {Promise<{status: string}>}
   */
  async deleteLED(deleteData) {
    try {
      const id = typeof deleteData === 'string' ? deleteData : deleteData.id;
      if (this.debug) {
        console.log('🗑️ [LED Service] Deleting LED device:', id);
      }
      
      const payload = {
        id: id,
        farmerId: deleteData.farmerId || '',
        farmId: deleteData.farmId || '',
        vendorId: this.vendorId
      };
      
      const url = `${this.baseUrl}/client/${this.clientId}/delete`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (this.debug) {
        console.log('✅ [LED Service] LED deleted successfully');
      }
      
      return {
        success: true,
        message: result.message || 'LED deleted successfully',
        data: result
      };
      
    } catch (error) {
      console.error('❌ [LED Service] Error deleting LED:', error);
      return {
        success: false,
        message: error.message || 'Failed to delete LED',
        error: error
      };
    }
  }
  
  /**
   * API 5: Control LED (turn on/off, adjust brightness)
   * Endpoint: POST /client/{clientId}/control-led
   *
   * The middleware accepts the command, publishes it on its message channel and
   * waits about 120 s for an Avialite device to confirm. When nothing answers it
   * replies "408 Fail get response" (every attempt since March 2026 ended this
   * way, last verified 2 Oct 2026). The command is sent regardless, so the
   * middleware receives and logs it, and the result says what happened instead
   * of throwing.
   *
   * @param {string} ledId - LED identifier
   * @param {string} stationId - Station identifier
   * @param {string} gatewayId - Gateway device ID
   * @param {string} cmd - Command ("ON" or "OFF")
   * @param {number} brightness - Brightness level (0-5)
   * @returns {Promise<{delivered: boolean, confirmed: boolean, status: string|null, message: string, httpStatus: number|null, seconds: number}>}
   *   delivered  - the middleware accepted the request: a 2xx reply, or the 408 it sends after waiting
   *                for a device; false for any other reply (401, 502, ...) or no reply
   *   confirmed  - the reply says status "success", i.e. an Avialite device answered
   *   status     - the status word of the middleware's reply ("success" / "fail"), null without a reply
   *   message    - the reply's message or body, or why nothing was delivered
   *   httpStatus - the HTTP status code, null without a reply
   *   seconds    - how long the request took
   */
  async controlLED(ledId, stationId, gatewayId, cmd, brightness = 0) {
    const startedAt = Date.now();
    const elapsed = () => Math.round((Date.now() - startedAt) / 1000);

    // Same 0-5 range as validateLEDData(); nothing is sent for a value outside it
    const level = parseInt(brightness);
    if (isNaN(level) || level < 0 || level > 5) {
      return {
        delivered: false,
        confirmed: false,
        status: null,
        message: 'Brightness must be between 0 and 5',
        httpStatus: null,
        seconds: 0
      };
    }

    // Abort the request when the middleware does not answer in time (same approach as listLEDs)
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CONTROL_TIMEOUT_MS);

    try {
      if (this.debug) {
        console.log(`🎛️ [LED Service] Controlling LED ${ledId}: ${cmd} (brightness ${level}/5)`);
      }

      const payload = {
        ledId,
        stationId,
        gatewayId,
        cmd,
        brightness: level
      };

      const url = `${this.baseUrl}/client/${this.clientId}/control-led`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      // The 408 body is plain text ("Fail get response"), so read text and parse it when it is JSON
      const text = await response.text();
      let body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch (e) {
        body = null;
      }
      const status = body && body.status != null ? String(body.status) : (response.ok ? null : 'fail');
      const confirmed = response.ok && status !== null && status.toLowerCase() === 'success';
      // An HTML error page (e.g. a proxy's "502 Proxy Error") is not a message, so only plain text is kept
      const plainText = text && !/^\s*</.test(text) ? text.trim() : '';
      // The middleware wraps some errors as {"error":{"statusCode","name","message"}}
      const message = (body && body.message)
        || (body && body.error && body.error.message)
        || (body ? status : plainText)
        || `HTTP ${response.status} ${response.statusText}`;
      // Delivered means published on the middleware's channel: a 2xx reply, or the reply the
      // middleware sends after waiting ~2 minutes for a device ("Fail get response", seen as
      // 408 text in its log and as a 400 JSON error envelope on the wire). Any other reply
      // (401, 502, ...) means the command never reached the channel.
      const waitedForDevice = /fail get response/i.test(String(message));
      const delivered = response.ok || response.status === 408 || waitedForDevice;

      if (this.debug) {
        console.log(`📥 [LED Service] Control reply after ${elapsed()} s: HTTP ${response.status}, status ${status}, delivered ${delivered}, confirmed ${confirmed}`);
      }

      return {
        delivered,
        confirmed,
        status,
        message,
        httpStatus: response.status,
        seconds: elapsed()
      };

    } catch (error) {
      console.error('❌ [LED Service] Error controlling LED:', error);
      return {
        delivered: false,
        confirmed: false,
        status: null,
        message: timedOut
          ? `no reply from the middleware within ${Math.round(CONTROL_TIMEOUT_MS / 1000)} seconds`
          : (error.message || 'network error'),
        httpStatus: null,
        seconds: elapsed()
      };
    } finally {
      clearTimeout(timer);
    }
  }
  
  /**
   * Utility: Validate LED data before registration
   * 
   * @param {Object} ledData - LED data to validate
   * @returns {Object} Validation result {valid: boolean, errors: string[]}
   */
  validateLEDData(ledData) {
    const errors = [];
    const required = ['ledId', 'stationId', 'gatewayId', 'farmerId', 'farmId'];
    
    required.forEach(field => {
      if (!ledData[field]) {
        errors.push(`${field} is required`);
      }
    });
    
    // Validate format (optional - adjust based on your requirements)
    if (ledData.ledId && ledData.ledId.length > 50) {
      errors.push('LED ID must be less than 50 characters');
    }
    
    if (ledData.brightness !== undefined) {
      const brightness = parseInt(ledData.brightness);
      if (isNaN(brightness) || brightness < 0 || brightness > 5) {
        errors.push('Brightness must be between 0 and 5');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Utility: Get LED status summary
   * 
   * @param {Array} leds - Array of LED devices
   * @returns {Object} Summary statistics
   */
  getLEDSummary(leds) {
    return {
      total: leds.length,
      online: leds.filter(led => led.status === 'Good' || led.cmd === 'ON').length,
      offline: leds.filter(led => led.status === 'Bad' || led.cmd === 'OFF').length,
      avgBrightness: leds.reduce((sum, led) => sum + (led.brightness || 0), 0) / leds.length || 0
    };
  }
}

export default new AvialiteService();
